/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {Group, DataStorage, EEmbededElementTypes, RenderDocument} from "js_renderer";
import {vec2, vec3} from "gl-matrix";
import Factory from "../factory";
import GeoEntity from "./geo_entity";
import {importHouseImage} from "App/dynamic_import";

export default
class TownEntity extends GeoEntity {

    public static TypeIcon: HTMLImageElement = new Image();
    public static TypeName: string = EEmbededElementTypes.TOWNENTITY;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        let textureUrl = '@assets/images/house.svg';
        importHouseImage().then((asset) => {
            let content =  asset.default ? asset.default : asset;
            if (content.indexOf("data:") !== -1) { // in url data
                TownEntity.TypeIcon.src =  content;
            }
            else {
                TownEntity.TypeIcon.src = content.replace(/\\/g, "/");
            }
        });

        // default values
        this.extendProperties({
            minSize: 6.0,
            maxSize: 12.0,
            pickable: false,
            textureUrl: textureUrl
        });
    }

    public static create(aRenderDocument: RenderDocument): TownEntity {
        let element = new TownEntity(aRenderDocument);
        element._typeName = TownEntity.TypeName;
        return element;
    }

    public setSize(aMinSize: number, aMaxSize: number) {
        this._properties.minPointSize = aMinSize;
        this._properties.maxPointSize = aMaxSize;
    }

    public getTypeIcon(): CanvasImageSource {
        return TownEntity.TypeIcon;
    }

    public getStyle(): string {
        return "bold";
    }

    public getColor(): vec3 {
        return vec3.fromValues(1.0, 1.0, 1.0);
    }

    public renderTest(aContext: CanvasRenderingContext2D, aVisibleArray: any[], aPosition: vec2, aDistanceFactor: number) {
        this._ro = {};
        this._ro.fontSize = this.getFontSize();
        this._ro.iconSize = this.getIconSize();
        this._ro.fillColor = this.getColor();
        this._ro.leg = true;
        this._ro.center = false;
        this._ro.border = 1;
        this._ro.x = aPosition[0];
        this._ro.y = aPosition[1];

        let horizontalOffset = 0;
        let verticalOffset = this.getVerticalOffset();

        // distance LOD
        if (aDistanceFactor < 0.09) {
            this._ro.fontSize -= 4;
            this._ro.iconSize -= 9;
        } else if (aDistanceFactor < 0.5) {
            this._ro.fontSize -= 2;
            this._ro.iconSize -= 5;
        }

        if (aDistanceFactor > 0.9) {
            this._ro.fontSize += 6;
            this._ro.border = 2;
            this._ro.iconSize = 0; // no icon shown
            this._ro.leg = false;
            this._ro.center = true;
        }

        // measure text
        aContext.font = this.getStyle() + " " + this._ro.fontSize + "pt " + this.getFont();
        this._ro.textWidth = aContext.measureText(this.getTitle()).width;
        this._ro.textHeight = this.getTextHeight(this.getFont(), this._ro.fontSize);

        if (aDistanceFactor > 0.9) { // compute text offset according to flag leg is shown (center text without a leg)
            horizontalOffset = -this._ro.textWidth * 0.5;
            verticalOffset = 20;
        }

        this._ro.iconSizeHalf = this._ro.iconSize * 0.5;
        this._ro.iconSizeQuat = this._ro.iconSizeHalf * 0.5;

        this._ro.textX = this._ro.x + this._ro.iconSizeHalf + this._ro.iconSizeQuat + horizontalOffset;
        this._ro.textY = this._ro.y - verticalOffset;

        if (this.isTextBBoxVisible(aContext, aVisibleArray, this._ro) && this._renderDocumentProperties.renderElement.getVisibility()) {
            aVisibleArray.push(this);
        }
    }

    // TODO: @ Jarek: the model element can contain more than one poi part, so we should generate more poi geometry for such model element
    protected _generateElementDescriptionJSON(): any {
        // these are model element properties taken from the scene api element properties
        return {
            type: this.getTypeName(),
            id: this.getTypeName() + "_" + this._UID,   // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            UID: this._UID
        };
    }

    // TODO: @ Jarek: the model element can contain more than one poi part, so we should generate more poi geometry for such model element
    protected _generateGeometryObjectDescriptionJSON(): any {
        let textBillboardGeometry =
            this._sceneAPI.getCanvasTextRenderer().createTextGeometry(
                this._properties.title,
                this._properties.position,
                this.getStyle(),
                this.getFontSize(),
                this.getFont(),
                this.getColor(),
                [0, 0, 0],
                TownEntity.TypeIcon.src,
                this.getIconSize()
            );

        return [{
            description: {
                type: "TEXT2D",
                color: [0.0, 0.0, 0.0],
                bboxMin: [this._properties.position[0], this._properties.position[1], this._properties.position[2]],
                bboxMax: [this._properties.position[0], this._properties.position[1], this._properties.position[2]]
            },
            pointSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: 1,
                binSize: 12,
                points: this._properties.position
            }
        }];
    }
}

Factory.register(TownEntity);
